import React, {useState} from 'react';
import {useParams, useNavigate} from 'react-router-dom';
import {useQuery} from '@apollo/client';
import {Button, Form, Input, InputNumber, Modal, Select, Spin, Table, Tag} from 'antd';
import {BCP_CHECK_JOB} from './graphql/queries/bcpCheckJobQueries';
import {useBcpCheckUserGroups} from './hooks/useBcpCheckUserGroups';
import {CheckJobDataSourceType, CheckJobDetailResponse} from './graphql/types/BcpCheckJobTypes';
const BcpCheckJobDetailPage: React.FC = () => {
    const {id} = useParams<{id: string}>();
    const navigate = useNavigate();
    const jobId = Number(id);
    const [form] = Form.useForm();
    const [selectedDs, setSelectedDs] = useState<CheckJobDataSourceType | null>(null);

    const {data, loading} = useQuery<CheckJobDetailResponse>(BCP_CHECK_JOB, {
        variables: {id: jobId},
        skip: !jobId,
    });

    const userGroupOptions = useBcpCheckUserGroups();

    const job = data?.bcpCheckJob?.data;

    React.useEffect(() => {
        if (job) {
            form.setFieldsValue({
                name: job.name,
                userGroupId: job.userGroupId,
                checkRule: job.checkRule,
                windowTime: job.windowTime,
                alarmReceiver: job.alarmReceiver,
                alarmMode: job.alarmMode,
                sampleRatio: job.sampleRatio,
                remark: job.remark,
            });
        }
    }, [job, form]);

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <Spin size="large" />
            </div>
        );
    }

    return (
        <div className="bg-white rounded-lg shadow-sm px-6 pb-6 pt-4">
            <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                    <Button onClick={() => navigate('/ops-tool/settlement/bcp/check-jobs')}>返回</Button>
                    <h2 className="text-base font-semibold m-0">
                        核对任务详情
                        <Tag className="ml-2" color={job?.status === 1 ? 'green' : 'default'}>
                            {job?.statusDesc || (job?.status === 1 ? '启用' : '禁用')}
                        </Tag>
                        <span className="ml-2 text-sm text-gray-400 font-normal">版本号: {job?.currVersion ?? '-'}</span>
                    </h2>
                </div>
            </div>

            <Form form={form} layout="vertical" disabled>
                <div className="max-w-2xl">
                    <Form.Item label="任务名称" name="name">
                        <Input placeholder="请输入任务名称" />
                    </Form.Item>
                    <Form.Item label="用户组" name="userGroupId">
                        <Select
                            placeholder="请选择用户组"
                            options={userGroupOptions}
                            allowClear
                            showSearch
                            filterOption={(input, option) =>
                                (option?.label as string)?.toLowerCase().includes(input.toLowerCase())
                            }
                        />
                    </Form.Item>
                </div>

                <div className="mb-4 max-w-3xl">
                    <div className="flex items-center justify-between mb-2">
                        <h3 className="text-sm font-medium m-0">数据源列表</h3>
                        <span className="text-xs text-gray-400">点击行查看详情</span>
                    </div>
                    <Table
                        dataSource={job?.dataSources || []}
                        columns={[
                            {title: '类型', dataIndex: 'type', key: 'type', width: 80, render: () => '数据库'},
                            {
                                title: '数据库',
                                key: 'database',
                                width: 150,
                                render: (_: any, record: CheckJobDataSourceType) => {
                                    try {
                                        const cfg = JSON.parse(record.config || '{}');
                                        return cfg.database || '-';
                                    } catch { return '-'; }
                                },
                            },
                            {title: '表名', dataIndex: 'tableName', key: 'tableName', width: 150},
                            {title: '过滤规则', dataIndex: 'filterRule', key: 'filterRule', ellipsis: true},
                        ]}
                        rowKey={(r) => String(r.id ?? Math.random())}
                        pagination={false}
                        size="small"
                        locale={{emptyText: '暂无数据源'}}
                        onRow={(record) => ({
                            onClick: () => setSelectedDs(record),
                            style: {cursor: 'pointer'},
                        })}
                    />
                </div>

                <div className="max-w-2xl">
                    <Form.Item label="核对规则" name="checkRule">
                        <Input.TextArea rows={3} placeholder="请输入核对规则" />
                    </Form.Item>
                    <Form.Item label="窗口时间(秒)" name="windowTime">
                        <InputNumber className="w-full" min={0} placeholder="请输入窗口时间" />
                    </Form.Item>
                    <Form.Item label="告警模式" name="alarmMode">
                        <Select placeholder="请选择告警模式">
                            <Select.Option value={1}>立即告警</Select.Option>
                            <Select.Option value={2}>汇总告警</Select.Option>
                        </Select>
                    </Form.Item>
                    <Form.Item label="采样率(%)" name="sampleRatio">
                        <InputNumber className="w-full" min={0} max={100} placeholder="请输入采样率" />
                    </Form.Item>
                    <Form.Item label="告警接收人" name="alarmReceiver">
                        <Input placeholder="请输入告警接收人" />
                    </Form.Item>
                    <Form.Item label="备注" name="remark">
                        <Input.TextArea rows={2} placeholder="请输入备注" />
                    </Form.Item>
                </div>
            </Form>

            <Modal
                title="数据源详情"
                open={selectedDs !== null}
                onCancel={() => setSelectedDs(null)}
                footer={<Button onClick={() => setSelectedDs(null)}>关闭</Button>}
                width={600}
                destroyOnClose
            >
                {selectedDs && (
                    <Form layout="vertical" disabled>
                        <Form.Item label="数据源类型">
                            <Input value="数据库" />
                        </Form.Item>
                        <Form.Item label="必须存在">
                            <Input value={selectedDs.mustExist ? '是' : '否'} />
                        </Form.Item>
                        <Form.Item label="数据库名">
                            <Input value={(() => { try { return JSON.parse(selectedDs.config || '{}').database || ''; } catch { return ''; } })()} />
                        </Form.Item>
                        <Form.Item label="表名">
                            <Input value={selectedDs.tableName || ''} />
                        </Form.Item>
                        <Form.Item label="使用字段">
                            <Input.TextArea rows={2} value={selectedDs.useFields || ''} />
                        </Form.Item>
                        <Form.Item label="分组规则">
                            <Input.TextArea rows={2} value={selectedDs.groupRule || ''} />
                        </Form.Item>
                        <Form.Item label="过滤规则">
                            <Input.TextArea rows={2} value={selectedDs.filterRule || ''} />
                        </Form.Item>
                    </Form>
                )}
            </Modal>
        </div>
    );
};

export default BcpCheckJobDetailPage;
